import React, { useCallback, useEffect, useState } from 'react';
import {
  isBtAvailable,
  getSavedBtPrinter,
  saveBtPrinter,
  clearBtPrinter,
  printBtBytes,
  SavedBtPrinter,
} from '../services/btPrinter';
import { btEnsurePermissions, btListDevices, BtDevice } from '../services/nativeBluetooth';
import { buildTestEscPos } from '../services/escpos';
import { usePos } from '../context/PosContext';
import './Sections.css';

// Bluetooth printer setup (bluetooth_activity_main.xml / device_list.xml). The
// Android app pairs classic Bluetooth thermal printers and streams ESC/POS over
// RFCOMM/SPP. Browsers can't reach classic Bluetooth, so this page is only
// functional inside the native app — pair the printer once in the phone's
// Bluetooth settings, then pick it here as the default receipt printer.

const BluetoothPrinter: React.FC = () => {
  const { config } = usePos();
  const native = isBtAvailable();
  const [devices, setDevices] = useState<BtDevice[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [printer, setPrinter] = useState<SavedBtPrinter | null>(() => getSavedBtPrinter());

  const isPrinter = (d: BtDevice) => !!printer && printer.address === d.address;

  const refresh = useCallback(async () => {
    if (!native) return;
    setError(null);
    setBusy(true);
    try {
      const granted = await btEnsurePermissions();
      if (!granted) {
        setError('Bluetooth permission was denied. Allow it in the system prompt and tap Refresh.');
        return;
      }
      const res = await btListDevices();
      setEnabled(res.enabled);
      setDevices(res.devices);
      if (!res.enabled) {
        setError('Bluetooth is turned off. Turn it on in your device settings, then tap Refresh.');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [native]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setAsPrinter = (d: BtDevice) => {
    const p: SavedBtPrinter = { address: d.address, label: d.name || 'Bluetooth Printer' };
    saveBtPrinter(p);
    setPrinter(p);
    setInfo(`Receipts & KOTs will print to ${p.label}.`);
  };

  const unsetPrinter = () => {
    clearBtPrinter();
    setPrinter(null);
    setInfo('Default Bluetooth printer cleared.');
  };

  const testPrint = async () => {
    setError(null);
    setInfo(null);
    try {
      await printBtBytes(buildTestEscPos(config.storeName));
      setInfo('Test page sent.');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (!native) {
    return (
      <div className="section-page">
        <div className="page-header">
          <h1>Bluetooth Printer</h1>
        </div>
        <div className="section-card">
          <p className="muted">
            Bluetooth thermal printing is only available in the Android app — browsers can't pair or
            stream to classic Bluetooth printers. Install the EPOS Android app to use a Bluetooth
            printer, or use <strong>USB Devices</strong> for thermal printing in the browser.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>Bluetooth Printer: {devices.length}</h1>
        <button
          className="btn-teal"
          onClick={refresh}
          disabled={busy}
          style={{ padding: '8px 16px', borderRadius: 6 }}
        >
          {busy ? 'Scanning…' : '⟳ Refresh'}
        </button>
      </div>

      <div className="section-card">
        <h2>Paired devices</h2>
        <p className="muted" style={{ marginBottom: 10 }}>
          Default print device:{' '}
          {printer ? (
            <>
              <strong>{printer.label || printer.address}</strong>{' '}
              <button className="btn-grey" onClick={testPrint}>
                Test Print
              </button>{' '}
              <button className="btn-grey" onClick={unsetPrinter}>
                Clear
              </button>
            </>
          ) : (
            <em>none — receipts/KOTs use the system print dialog.</em>
          )}
        </p>
        {error && <p style={{ color: 'var(--color-accent)' }}>{error}</p>}
        {info && <p style={{ color: 'var(--color-primary-dark)' }}>{info}</p>}
        <p className="muted" style={{ marginBottom: 10, fontSize: 13 }}>
          Pair the printer first in your phone's <strong>Settings → Bluetooth</strong>, then it
          appears below.
        </p>
        {!enabled ? (
          <p className="muted">Bluetooth is off. Turn it on, then tap Refresh.</p>
        ) : devices.length === 0 ? (
          <p className="muted">
            No paired Bluetooth devices found. Pair your printer in the system Bluetooth settings,
            then tap Refresh.
          </p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.address}>
                  <td>🖨️ {d.name || 'Bluetooth Device'}</td>
                  <td>{d.address}</td>
                  <td>
                    {isPrinter(d) ? (
                      <button className="btn-grey" onClick={unsetPrinter}>
                        ★ Printer
                      </button>
                    ) : (
                      <button className="btn-teal" onClick={() => setAsPrinter(d)}>
                        Set as Printer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BluetoothPrinter;
